import React, { useRef } from "react";
import styled from "styled-components";
import Link from "../../UI/elements/Link";
import { theme } from "../../../styles/theme/theme";
import { translate } from "../../../config/i18n";

/* phone: one row scrolled sideways, out to the screen edge; desktop: the content column, wrapping */
const Row = styled.ul`
  display: flex;
  gap: ${theme.spaces.margin.medium};
  margin: ${theme.spaces.margin.xl} 0 0;
  padding: 0 ${theme.layout.pageGutter};
  list-style: none;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${theme.mq.desktop} {
    flex-wrap: wrap;
    gap: ${theme.spaces.margin.large};
    width: min(${theme.layout.contentWidth}, 100% - 2 * ${theme.layout.pageGutter});
    margin: ${theme.spaces.padding.big} auto 0;
    padding: 0;
    overflow-x: visible;
  }
`;

/* 32 px tall on a phone, 41 at 1920: the height follows the type (1.8em) */
export const Pill = styled(Link)`
  ${theme.type.subcategoryPill}
  display: inline-flex;
  align-items: center;
  min-height: 1.8em;
  padding: 0 ${theme.spaces.padding.medium};
  white-space: nowrap;
  text-transform: lowercase;
  text-decoration: none;
  color: ${theme.colors.titles};
  background: ${theme.colors.gray};
  border-radius: ${theme.radius.pill};

  &:hover,
  &[aria-current="page"] {
    color: ${theme.colors.white};
    background: ${theme.colors.primary};
  }
`;

interface PillsPosition {
  pillsTop: number;
  pillsScrollLeft: number;
}

const PILLS_ID = "subcategory-pills";

/**
 * Called from `shouldUpdateScroll`: after a pill click the new page's row is put where the old one was on screen
 * (and scrolled sideways the same), instead of the jump to the top. The marker is then dropped from the history entry,
 * so back/forward to it gets Gatsby's own scroll restoring. Returns undefined for any other navigation (Gatsby's default).
 */
export const keepPillsInPlace = (state: unknown): false | undefined => {
  const pills = document.getElementById(PILLS_ID);
  const position = state as Partial<PillsPosition> | null;
  if (!pills || typeof position?.pillsTop !== "number") {
    return undefined;
  }
  window.history.replaceState({ ...(window.history.state as object), pillsTop: undefined }, "");
  pills.scrollLeft = position.pillsScrollLeft ?? 0;
  window.scrollTo(0, window.scrollY + pills.getBoundingClientRect().top - position.pillsTop);
  return false;
};

export interface SubcategoryLink {
  to: string;
  name: string;
  count?: number;
}

/** The subcategory pages of the current category group (or the blog's tags); the page's own one is marked by Gatsby Link (aria-current). */
const SubcategoryNav = ({ items, label = translate("category.subcategories.label") }: { items: SubcategoryLink[]; label?: string }) => {
  const row = useRef<HTMLUListElement>(null);
  // Link reads `state` when clicked, after onClick has filled it in
  const position = useRef<PillsPosition>({ pillsTop: 0, pillsScrollLeft: 0 });
  const measure = () => {
    if (row.current) {
      position.current.pillsTop = row.current.getBoundingClientRect().top;
      position.current.pillsScrollLeft = row.current.scrollLeft;
    }
  };

  return (
    <nav aria-label={label}>
      <Row id={PILLS_ID} ref={row}>
        {items.map((item) => (
          <li key={item.to}>
            <Pill to={item.to} state={position.current} onClick={measure}>
              {item.name}
              {item.count !== undefined && ` (${item.count})`}
            </Pill>
          </li>
        ))}
      </Row>
    </nav>
  );
};

export default SubcategoryNav;
